import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import InvoiceSheet, { InvoicePrintStyles } from '../components/InvoiceSheet';
import BillingChargePanel from '../components/BillingChargePanel';
import apiService from '../services/api';
import { isNonBillableTxn } from '../utils/transactionRevenue';
import {
  buildMerchantIndex,
  commissionFor,
  merchantRate,
  matchBillableMerchant,
  monthKeyFromTxn,
  txnDate, invoiceTotalFor, stripeFeeFor } from '../utils/billing';
import { canAccessMerchantPay, getPortalPathForUser, isMerchantSpecialistUser } from '../utils/portalRouting';
import '../styles/StaffPages.css';

/**
 * The invoice for one merchant Homecrowd signed, for one month.
 *
 * Keyed by the merchant record's id rather than by name, so a merchant whose
 * transactions carry more than one spelling still bills as one account.
 */
const SuperuserBillingInvoice = () => {
  const { yearMonth, merchantId } = useParams();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const user = await apiService.fetchCurrentUser();
        if (!canAccessMerchantPay(user)) {
          navigate(getPortalPathForUser(user));
          return;
        }
        setCurrentUser(user);
        const [txnRes, merchantRes] = await Promise.all([
          apiService.getAllTransactionsSuperuser(),
          apiService.getBillableMerchants(),
        ]);
        setTransactions(txnRes?.transactions || []);
        setMerchants(Array.isArray(merchantRes?.results) ? merchantRes.results : []);
      } catch (err) {
        console.error('Failed to load invoice data:', err);
        setError('Failed to load transactions.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const merchant = useMemo(
    () => merchants.find((m) => String(m.id) === String(merchantId)) || null,
    [merchants, merchantId]
  );

  const lineItems = useMemo(() => {
    if (!merchant) return [];
    const index = buildMerchantIndex(merchants);
    const rate = merchantRate(merchant);
    return transactions
      .filter((t) => {
        // Never bill a failed, refunded or voided transaction: no money was
        // kept, so no commission is owed. Kept out of the lines as well as the
        // total, so the invoice a merchant reads reconciles with the charge.
        if (isNonBillableTxn(t)) return false;
        if (monthKeyFromTxn(t) !== yearMonth) return false;
        const matched = matchBillableMerchant(t, index);
        return matched && String(matched.id) === String(merchant.id);
      })
      .map((t) => {
        const amount = Number(t.amount || 0);
        const commission = commissionFor(t, merchant);
        return {
          id: t.id || t.external_txn_id,
          date: txnDate(t),
          status: t.status,
          amount,
          commission,
          rate,
        };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [transactions, merchants, merchant, yearMonth]);

  const totals = useMemo(() => {
    const totalSales = lineItems.reduce((s, l) => s + l.amount, 0);
    const totalCommission = lineItems.reduce((s, l) => s + l.commission, 0);
    return {
      totalSales,
      totalCommission,
      blendedRate: merchantRate(merchant),
      count: lineItems.length,
    };
  }, [lineItems, merchant]);

  // The processor's cut for collecting this invoice. Charged to the merchant,
  // so it belongs on the sheet and in the debit — computed once here and used
  // for both, rather than in two places that could drift.
  const stripeFee = useMemo(
    () => stripeFeeFor(totals.totalCommission),
    [totals.totalCommission],
  );

  const navSection = isMerchantSpecialistUser(currentUser) ? 'merchant-pay' : 'superuser';

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection={navSection} />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection={navSection} />
      <div className="admin-main">
        <div className="admin-content">
          <InvoiceSheet
            backTo="/superuser/invoices/"
            backText="Back to Merchant Pay"
            banner={merchant && (
              <BillingChargePanel
                merchantKey={merchant.id}
                merchantName={merchant.name}
                period={yearMonth}
                amountCents={Math.round(
                  invoiceTotalFor(totals.totalCommission) * 100,
                )}
                lineItems={lineItems}
              />
            )}
            merchantName={merchant ? merchant.name : 'Unknown merchant'}
            yearMonth={yearMonth}
            lineItems={lineItems}
            totals={totals}
            feeLabel="Stripe ACH fee (0.8%)"
            feeAmount={stripeFee}
            error={
              error ||
              (!merchant ? 'This merchant is no longer billable.' : null)
            }
          />
        </div>
      </div>
      <InvoicePrintStyles />
    </div>
  );
};

export default SuperuserBillingInvoice;
